import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import type { AppConfig } from '../config';

import type { Logger } from '@seek/logger';
import {
  type CandidateService,
  createCandidateService,
} from './candidateService/candidateService';
import { createCandidateServiceMock } from './candidateService/candidateService.mock';
import {
  type CompanyDetailsService,
  createCompanyDetailsService,
} from './companyDetailsService/companyDetailsService';
import { createCompanyDetailsServiceMock } from './companyDetailsService/companyDetailsService.mock';
import {
  type CompanyProfileService,
  createCompanyProfileService,
} from './companyProfileService/companyProfileService';
import { createCompanyProfileServiceMock } from './companyProfileService/companyProfileService.mock';
import {
  type CompanyReviewService,
  createCompanyReviewService,
} from './companyReviewService/companyReviewService';
import { createCompanyReviewServiceMock } from './companyReviewService/companyReviewService.mock';
import {
  createLocationService,
  type LocationService,
} from './locationService/locationService';
import { createLocationServiceMock } from './locationService/locationService.mock';
import { createRoleService, type RoleService } from './roleService/roleService';
import { createRoleServiceMock } from './roleService/roleService.mock';
import {
  createSitemapService,
  type SitemapService,
} from './sitemapService/sitemapService';
import { createSitemapServiceMock } from './sitemapService/sitemapService.mock';

export interface Services {
  candidateService: CandidateService;
  companyDetailsService: CompanyDetailsService;
  companyProfileService: CompanyProfileService;
  companyReviewService: CompanyReviewService;
  locationService: LocationService;
  roleService: RoleService;
  sitemapService: SitemapService;
}

export const createServices = ({
  client,
  config,
  logger,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  config: AppConfig;
  logger: Logger;
}): Services => {
  if (config.environment === 'local') {
    return {
      companyProfileService: createCompanyProfileServiceMock({ config }),
      companyReviewService: createCompanyReviewServiceMock(),
      candidateService: createCandidateServiceMock(),
      roleService: createRoleServiceMock(),
      sitemapService: createSitemapServiceMock({ config }),
      locationService: createLocationServiceMock(),
      companyDetailsService: createCompanyDetailsServiceMock({ config }),
    };
  }

  if (config.environment === 'development') {
    return {
      companyProfileService: createCompanyProfileService({
        client,
        config,
        logger,
      }),
      companyReviewService: createCompanyReviewService({ client, logger }),
      candidateService: createCandidateService({ client, config }),
      roleService: createRoleService({ client, config }),
      sitemapService: createSitemapServiceMock({ config }),
      locationService: createLocationService({ client, config }),
      companyDetailsService: createCompanyDetailsService({
        client,
        config,
        logger,
      }),
    };
  }

  return {
    companyProfileService: createCompanyProfileService({
      client,
      config,
      logger,
    }),
    companyReviewService: createCompanyReviewService({ client, logger }),
    candidateService: createCandidateService({ client, config }),
    roleService: createRoleService({ client, config }),
    sitemapService: createSitemapService({ config }),
    locationService: createLocationService({ client, config }),
    companyDetailsService: createCompanyDetailsService({
      client,
      config,
      logger,
    }),
  };
};

export {
  createLocationSlugService,
  type LocationDetails,
  type LocationSlugService,
} from './locationService/locationSlugService';
